import { Injectable } from '@angular/core';
import { TranslationService, Classification } from '@spartacus/core';
import {
  GenericAttr,
  ProducSpecsDoubleColumn,
  ProducSpecsDoubleColumnNormalized,
  ProducSpecsSingleColumn,
  TABLE_SPEC_TYPE,
} from './product-specifications.model';
import { LoadCurveClassification, SpeedCurveClassification } from '../../../../../core/models/product.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProductSpecificationsService {
  constructor(private translationService: TranslationService) {}

  identifySpecs(table: ProducSpecsSingleColumn | ProducSpecsDoubleColumn): TABLE_SPEC_TYPE {
    if (table.attributes.length > 0) {
      const firstAttr = table.attributes[0];

      if ('key' in firstAttr && 'value' in firstAttr) {
        return TABLE_SPEC_TYPE.DOUBLE_ATTRS;
      }

      if (typeof firstAttr === 'object' && !Array.isArray(firstAttr)) {
        return TABLE_SPEC_TYPE.MULTI_COLUMN;
      }
    }

    return TABLE_SPEC_TYPE.MULTI_COLUMN;
  }

  normalizeProductClassifications(classification: Classification): ProducSpecsDoubleColumn {
    return {
      uid: classification.code || '',
      title: classification.name || classification.code || '',
      attributes: (classification.features || [])?.map(feature => {
        const _value = (feature.featureValues || [])[0]?.value || '';
        const _unit = feature.featureUnit?.symbol || '';

        return {
          key: feature.name || '',
          value: `${_value} ${_unit}`,
        };
      }),
    };
  }

  async normalizeLoadCurveClassification(items: LoadCurveClassification[]): Promise<ProducSpecsSingleColumn> {
    const sortByLoad = (data: LoadCurveClassification[]) => {
      return [...data].sort((a, b) => parseFloat(a.load) - parseFloat(b.load));
    };

    const sortedItems = sortByLoad(items).map(item => {
      return {
        ...item,
        ...{
          load: (parseFloat(item.load) * 100).toFixed(0).toString() + '%',
        },
      };
    });

    const attributes: {
      [key: string]: GenericAttr;
    }[] = sortedItems.map((item, index) => {
      const result: Record<string, { key: string; value: string }> = {};

      if (index === 0) {
        result[`index_0`] = { key: 'perc_rated_load', value: 'current_amp' };

        sortedItems.forEach((pair, index) => {
          result[`index_${index + 1}`] = { key: pair.load, value: pair.amps };
        });
      }

      if (index === 1) {
        result[`index_0`] = { key: 'perc_rated_load', value: 'rated_output' };

        sortedItems.forEach((pair, index) => {
          result[`index_${index + 1}`] = { key: pair.load, value: pair.kw };
        });
      }

      if (index === 2) {
        result[`index_0`] = { key: 'perc_rated_load', value: 'efficiency_pencentage' };

        sortedItems.forEach((pair, index) => {
          result[`index_${index + 1}`] = { key: pair.load, value: pair.eff };
        });
      }

      if (index === 3) {
        result[`index_0`] = { key: 'perc_rated_load', value: 'power_factor_percentage' };

        sortedItems.forEach((pair, index) => {
          result[`index_${index + 1}`] = { key: pair.load, value: pair.powerfactor };
        });
      }

      if (index === 4) {
        result[`index_0`] = { key: 'perc_rated_load', value: 'speed_rpm' };

        sortedItems.forEach((pair, index) => {
          result[`index_${index + 1}`] = { key: pair.load, value: pair.rpm };
        });
      }

      if (index === 5) {
        result[`index_0`] = { key: 'perc_rated_load', value: 'temp_rise_fl' };

        sortedItems.forEach((pair, index) => {
          result[`index_${index + 1}`] = { key: pair.load, value: pair.risebyresis };
        });
      }

      if (index === 6) {
        result[`index_0`] = { key: 'perc_rated_load', value: 'torque_ft_lbs' };

        sortedItems.forEach((pair, index) => {
          result[`index_${index + 1}`] = { key: pair.load, value: pair.torque };
        });
      }

      return result;
    });

    const title = await firstValueFrom(this.translationService.translate('productSpecifications.loadCurve'));
    const translatedAttributes = await Promise.all(
      attributes.map(async (attr: any) => {
        const index_0 = {
          key: await firstValueFrom(
            this.translationService.translate('productSpecifications.index_0.' + attr.index_0.key),
          ),
          value: await firstValueFrom(
            this.translationService.translate('productSpecifications.index_0.' + attr.index_0.value),
          ),
        };

        return {
          ...attr,
          index_0,
        };
      }),
    );

    return {
      uid: 'LoadCurve',
      title,
      attributes: translatedAttributes,
    };
  }

  async normalizeSpeedCurveClassification(items: SpeedCurveClassification[]): Promise<ProducSpecsSingleColumn> {
    const attributes: {
      [key: string]: GenericAttr;
    }[] = await Promise.all(
      items.map(async item => {
        const result: Record<string, { key: string; value: string }> = {};

        result[`index_0`] = {
          key: await firstValueFrom(this.translationService.translate('productSpecifications.titles.curvepoint')),
          value: item.curvepoint,
        };
        result[`index_1`] = {
          key: await firstValueFrom(this.translationService.translate('productSpecifications.titles.current_amps')),
          value: item.amps,
        };
        result[`index_2`] = {
          key: await firstValueFrom(
            this.translationService.translate('productSpecifications.titles.current_percentage'),
          ),
          value: item.ampspct,
        };
        result[`index_3`] = {
          key: await firstValueFrom(this.translationService.translate('productSpecifications.titles.torque_ft_lbs')),
          value: item.torque,
        };
        result[`index_4`] = {
          key: await firstValueFrom(
            this.translationService.translate('productSpecifications.titles.torque_percentage'),
          ),
          value: item.torquepct,
        };
        result[`index_5`] = {
          key: await firstValueFrom(this.translationService.translate('productSpecifications.titles.speed_rpm')),
          value: item.rpm,
        };
        result[`index_6`] = {
          key: await firstValueFrom(this.translationService.translate('productSpecifications.titles.rated_output_kw')),
          value: item.kw,
        };
        result[`index_7`] = {
          key: await firstValueFrom(
            this.translationService.translate('productSpecifications.titles.power_factor_percentage'),
          ),
          value: item.powerfactor,
        };
        result[`index_8`] = {
          key: await firstValueFrom(this.translationService.translate('productSpecifications.titles.rated_output_hp')),
          value: item.hp,
        };

        return result;
      }),
    );

    const title = await firstValueFrom(this.translationService.translate('productSpecifications.speedCharacteristics'));

    return {
      uid: 'speedCurve',
      title,
      attributes,
    };
  }

  normalizeTabDoubleCollumn(input: ProducSpecsDoubleColumn): ProducSpecsDoubleColumnNormalized {
    const normalizedAttributes = [];

    for (let i = 0; i < input.attributes.length; i += 2) {
      const attr1 = input.attributes[i];
      const attr2 = input.attributes[i + 1] ? input.attributes[i + 1] : '';

      normalizedAttributes.push({
        key_01: attr1.key,
        value_01: attr1.value,
        key_02: attr2 ? attr2.key : '',
        value_02: attr2 ? attr2.value : '',
      });
    }

    return {
      uid: input.uid,
      title: input.title,
      attributes: normalizedAttributes,
    };
  }
}
